import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Skeleton, LinearProgress, Select, MenuItem, FormControl } from "@mui/material";
import { fetchData, HTTP_STATUS, intl, DmsAltBox } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";

const BarItem = ({ label, value, maxValue, onClick, isSelected, color = "primary" }) => {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

    return (
        <Box
            onClick={onClick}
            sx={{
                cursor: onClick ? "pointer" : "default",
                p: 1,
                borderRadius: 1,
                backgroundColor: isSelected ? `${color}.light` : "transparent",
                border: isSelected ? "2px solid" : "2px solid transparent",
                borderColor: isSelected ? `${color}.main` : "transparent",
                transition: "all 0.2s ease",
                "&:hover": onClick
                    ? {
                          backgroundColor: `${color}.light`,
                          opacity: 0.9,
                      }
                    : {},
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography
                    variant="body2"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "70%",
                    }}
                    title={label}
                >
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight="bold" color={`${color}.main`}>
                    {value.toLocaleString("pt-BR")}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${color}.light`,
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: `${color}.main`,
                        borderRadius: 4,
                    },
                }}
            />
        </Box>
    );
};

BarItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    maxValue: PropTypes.number.isRequired,
    onClick: PropTypes.func,
    isSelected: PropTypes.bool,
    color: PropTypes.string,
};

export const DmsAnalyticsBar = ({
    url,
    title,
    crossFilter: crossFilterKey,
    labelKey = "name",
    valueKey = "value",
    pkKey = "pk",
    maxItems = 10,
    color = "primary",
    metricOptions,
    defaultMetric = "sales",
    metric, // Global metric override - takes precedence over local state
    children,
    sx,
    ...props
}) => {
    const { queryParams, crossFilters, toggleCrossFilter, refreshKey } = useDmsAnalyticsContext();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState(defaultMetric);

    // Use global metric prop if provided, otherwise use local state
    const effectiveMetric = metric || selectedMetric;

    const loadData = useCallback(async () => {
        if (!url) return;

        setLoading(true);
        try {
            const separator = url.includes("?") ? "&" : "?";
            let fullUrl = queryParams ? `${url}${separator}${queryParams}` : url;

            // Add metric param if global metric is provided OR if metricOptions are provided
            if (effectiveMetric) {
                const metricSeparator = fullUrl.includes("?") ? "&" : "?";
                fullUrl = `${fullUrl}${metricSeparator}metric=${effectiveMetric}`;
            }

            const response = await fetchData(fullUrl, "GET", true);

            if (response.status === HTTP_STATUS.OK_200) {
                const responseData = response.data?.data;
                setData(responseData?.items || responseData || []);
            }
        } catch (err) {
            console.error(`Error loading data from ${url}:`, err);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [url, queryParams, effectiveMetric]);

    useEffect(() => {
        loadData();
    }, [loadData, refreshKey]);

    // Toggle cross-filter: click adds, click again removes
    const handleBarClick = useCallback((item) => {
        if (crossFilterKey) {
            const value = crossFilterKey === "uf" ? (item[labelKey] || item.name) : item[pkKey];
            toggleCrossFilter(crossFilterKey, value, item[labelKey] || item.name);
        }
    }, [crossFilterKey, toggleCrossFilter, labelKey, pkKey]);

    // Check if item is selected (multi-select support)
    const isSelected = (item) => {
        const items = crossFilters?.[crossFilterKey] || [];
        if (items.length === 0) return false;

        const itemValue = crossFilterKey === "uf" ? (item[labelKey] || item.name) : item[pkKey];
        return items.some((cf) => cf.value === itemValue);
    };

    const safeData = Array.isArray(data) ? data : [];
    const displayData = safeData.slice(0, maxItems);
    const maxValue = Math.max(...displayData.map((item) => item[valueKey] || 0), 1);

    const handleMetricChange = (event) => {
        setSelectedMetric(event.target.value);
    };

    return (
        <DmsAltBox.Card sx={{ height: "100%", ...sx }} {...props}>
            <DmsAltBox.Header>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <Typography variant="h6" sx={{ color: "common.white" }}>
                        {title}
                    </Typography>
                    {metricOptions && metricOptions.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value={selectedMetric}
                                onChange={handleMetricChange}
                                variant="standard"
                                sx={{
                                    color: "common.white",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    "&:before": { borderBottom: "1px solid rgba(255,255,255,0.5)" },
                                    "&:after": { borderBottom: "2px solid white" },
                                    "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid white" },
                                    "& .MuiSelect-icon": { color: "common.white" },
                                }}
                            >
                                {metricOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                {children && <DmsAltBox.HeaderRight>{children}</DmsAltBox.HeaderRight>}
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                <Box sx={{ minHeight: 280 }}>
                    {loading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <Box key={index}>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="rectangular" height={8} />
                                </Box>
                            ))}
                        </Box>
                    ) : displayData.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: 200,
                                color: "text.secondary",
                            }}
                        >
                            <Typography variant="body2">
                                {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {displayData.map((item, index) => (
                                <BarItem
                                    key={item[pkKey] || item[labelKey] || index}
                                    label={item[labelKey]}
                                    value={item[valueKey]}
                                    maxValue={maxValue}
                                    onClick={crossFilterKey ? () => handleBarClick(item) : null}
                                    isSelected={isSelected(item)}
                                    color={color}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};

DmsAnalyticsBar.propTypes = {
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    crossFilter: PropTypes.string,
    labelKey: PropTypes.string,
    valueKey: PropTypes.string,
    pkKey: PropTypes.string,
    maxItems: PropTypes.number,
    color: PropTypes.string,
    metricOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    defaultMetric: PropTypes.string,
    metric: PropTypes.string, // Global metric override
    children: PropTypes.node,
    sx: PropTypes.object,
};
